import { Skeleton } from '@mui/material'
import { DropdownValue } from 'ext-ui/components/Dropdown/hooks/DropdownState.reducer'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectTransactionIdFromPathState } from 'src/app/TransactionDetail/redux/transactionDetail.selectors'
import Box from 'src/components/Box/Box'
import DisabledExpenseDetailsTooltip from 'src/components/DisabledExpenseDetailsTooltip/DisabledExpenseDetailsTooltip'
import SvgTransactionPushPending from 'src/components/Svgs/SvgTransactionPushPending'
import TransactionExpenseDetailsFormV2 from 'src/components/TransactionExpenseDetailsFormV2/TransactionExpenseDetailsFormV2'
import Trns from 'src/localization/Trns'
import { ConnectedPlatformStatus } from 'src/models/transaction.models'
import {
  useQueryActiveConnectedPlatformName,
  useQueryActiveTransactionIntegration,
} from 'src/queries/integrations/orgIntegrations.queries'
import {
  activeAlphabeticalExpenseCategoryFiltersRequestParams,
  useQueryExpenseCategories,
} from 'src/queries/organizations/expenseCategories.queries'
import { useQueryTransactionDetail } from 'src/queries/transactions.queries'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'

import * as styles from './TransactionExpenseDetailsBox.style'

interface TransactionExpenseDetailsBoxProps {
  className?: string
  testId?: string
}

const TransactionExpenseDetailsBox: React.FC<
  TransactionExpenseDetailsBoxProps
> = (props): React.ReactElement => {
  const transactionId = useSelector(selectTransactionIdFromPathState)

  const { data: transaction } = useQueryTransactionDetail({ transactionId })
  const { analytics } = useAnalyticsContext()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const { data: activeConnectedPlatformName } =
    useQueryActiveConnectedPlatformName(transactionId)
  const { data: activeTransactionIntegration } =
    useQueryActiveTransactionIntegration(transactionId)
  const { data: expCatSchema, isPending: expCatSchemaLoading } =
    useQueryExpenseCategories({
      filters: activeAlphabeticalExpenseCategoryFiltersRequestParams,
    })

  const onSuccessfulSubmit = (
    categoryId: string,
    selectedValue: DropdownValue | undefined,
    isSuggested?: boolean,
  ): void => {
    analytics?.track(
      'transaction detail - edit transaction vendor and expense categories - button press',
      {
        intent: 'edit transaction vendor and expense categories',
        interaction: 'button press',
        screen: calculatedBackgroundLocation.pathname,
        expenseDetails: [
          {
            categoryId,
            labelId: selectedValue?.value ?? '',
            isSuggested: isSuggested ?? false,
          },
        ],
      },
    )
  }

  const syncedWithIntegration = useMemo(
    () =>
      activeTransactionIntegration !== undefined &&
      [
        ConnectedPlatformStatus.Pending,
        ConnectedPlatformStatus.Success,
      ].includes(activeTransactionIntegration.syncStatus),
    [activeTransactionIntegration],
  )

  if (expCatSchemaLoading) {
    return (
      <Box
        className={props.className}
        css={styles.container}
        testId={props.testId}
      >
        <div className={'flex-column gap-5'}>
          <Skeleton variant={'rounded'} css={styles.skeletonTitle} />
          {[1, 2, 3].map((_, idx) => (
            <div key={idx} className={'flex-column gap-2'}>
              <Skeleton variant={'rounded'} css={styles.skeletonCategory} />
              <Skeleton variant={'rounded'} css={styles.skeletonLabel} />
            </div>
          ))}
        </div>
      </Box>
    )
  }

  if (!expCatSchema) {
    return <></>
  }

  return (
    <Box
      className={props.className}
      css={styles.container}
      testId={props.testId}
    >
      <div css={styles.header}>
        <span css={styles.title}>
          <Trns source={'Expense Categories'} />
        </span>

        {syncedWithIntegration && (
          <span css={styles.syncStatus}>
            {activeTransactionIntegration?.syncStatus ===
              ConnectedPlatformStatus.Pending && (
              <>
                <span css={styles.syncStatusText}>
                  <Trns source={'Sync in progress'} />
                </span>
                <SvgTransactionPushPending />
              </>
            )}
            {activeTransactionIntegration?.syncStatus ===
              ConnectedPlatformStatus.Success && (
              <>
                <span css={styles.syncStatusText}>
                  <Trns
                    source={'Synced to ${orgLvlIntegration}'}
                    expressions={{
                      orgLvlIntegration: activeConnectedPlatformName ?? '',
                    }}
                  />
                </span>
                <DisabledExpenseDetailsTooltip
                  integration={activeConnectedPlatformName}
                  placement={'bottom-end'}
                />
              </>
            )}
          </span>
        )}
      </div>

      <TransactionExpenseDetailsFormV2
        expCatSchema={expCatSchema}
        transactionId={transaction?.id}
        expenseDetails={transaction?.expenseDetails}
        onExpenseCategorySelectSuccess={onSuccessfulSubmit}
      />
    </Box>
  )
}

export default TransactionExpenseDetailsBox
